import { useEffect, useRef, useState } from 'react'

import { useAuth } from 'src/auth'
import {
  TURNSTILE_INTERACTION_MESSAGE,
  useTurnstileToken,
  UseTurnstileTokenResult,
} from 'src/components/Turnstile/useTurnstileToken'
import { setDriverToken } from 'src/lib/driverAuth'
import { removeCountryCode, unformatPhoneNumber } from 'src/lib/phone.utils'

import {
  trackCodeRequested,
  trackCodeResult,
  trackSendSkipped,
} from './loginTelemetry'
import {
  getCooldownEndsAt,
  recordCodeSent,
  wasCodeRecentlySent,
} from './smsSendLimit'

export interface UseDriverEmbeddedAuthOptions {
  /** Slug forwarded to the start call so the BE can attribute the attempt. */
  companySlug?: string
  /** Company resolved by the page (via slug or applicationId); forwarded so the
   *  BE has the company even when no slug is present (e.g. application links). */
  companyId?: string
  /** Phone-input value to seed the field with (already country-code aware). */
  initialPhone?: string
  /** When the initial phone is submittable, send the code and open the OTP
   *  step immediately — lets a caller skip phone entry entirely. */
  autoStart?: boolean
  /** The session is set + reauthenticated before this fires; the consumer only
   *  decides where to navigate next. May be async (e.g. resolve a destination);
   *  the loading state is held until it settles. */
  onVerified: (result: {
    e164: string
    accessToken: string
  }) => void | Promise<void>
  /** Brand-new phone (Auth0 signup disabled). The consumer resolves the
   *  destination (e.g. the public short form); throw to surface an error. */
  onNewUser?: (e164: string) => void | Promise<void>
}

export interface UseDriverEmbeddedAuthResult {
  step: 'phone' | 'otp'
  phone: string
  otp: string
  loading: boolean
  error: string | null
  canSubmitPhone: boolean
  cooldownSeconds: number
  phoneBlocked: boolean
  setPhone: (value: string) => void
  setOtp: (value: string) => void
  sendCode: () => Promise<void>
  verifyCode: (code?: string) => Promise<void>
  editNumber: () => void
  /** Mount <TurnstileWidget> with these so `sendCode` can attach a token. */
  turnstile: UseTurnstileTokenResult
}

/**
 * The embedded SMS auth state machine — start/verify against /api/embeddedLogin,
 * step + loading + error handling, and persisting the driver session. It never
 * navigates: routing is the consumer's job via onVerified / onNewUser.
 */
export const useDriverEmbeddedAuth = ({
  companySlug,
  companyId,
  initialPhone,
  autoStart,
  onVerified,
  onNewUser,
}: UseDriverEmbeddedAuthOptions): UseDriverEmbeddedAuthResult => {
  const { reauthenticate } = useAuth()

  const [step, setStep] = useState<'phone' | 'otp'>('phone')
  const [phone, setPhone] = useState(initialPhone ?? '')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A request in flight. State alone can't guard this: two taps (or a tap and
  // an Enter key) in the same tick both see loading=false. Cloudflare's rate
  // limit on /api/embeddedLogin caught a driver doing exactly that on
  // 2026-10-05 — 3 requests in one second.
  const inFlightRef = useRef(false)
  // sendCode awaits the Turnstile token before `call` claims inFlightRef, so
  // it needs its own guard for taps that land during that wait.
  const sendingRef = useRef(false)
  // Error text from the last failed `call`, for telemetry (state lags a tick).
  const lastErrorRef = useRef<string | null>(null)
  const [cooldownEndsAt, setCooldownEndsAt] = useState(() =>
    getCooldownEndsAt()
  )
  const [now, setNow] = useState(() => Date.now())

  // Proof a real browser is asking for the code; verified by /api/embeddedLogin.
  const turnstile = useTurnstileToken('driver-login')

  const e164 = unformatPhoneNumber(phone) || ''
  const nationalDigits = removeCountryCode(phone)?.replace(/\D/g, '') || ''
  const canSubmitPhone = nationalDigits.length >= 10
  const cooldownSeconds =
    cooldownEndsAt && cooldownEndsAt > now
      ? Math.ceil((cooldownEndsAt - now) / 1000)
      : 0
  const phoneBlocked = cooldownSeconds > 0 && !wasCodeRecentlySent(e164, now)

  useEffect(() => {
    if (!cooldownEndsAt) return
    const id = setInterval(() => {
      const tick = Date.now()
      setNow(tick)
      if (tick >= cooldownEndsAt) setCooldownEndsAt(getCooldownEndsAt(tick))
    }, 1000)
    return () => clearInterval(id)
  }, [cooldownEndsAt])

  const refreshCooldown = () => {
    const tick = Date.now()
    setNow(tick)
    setCooldownEndsAt(getCooldownEndsAt(tick))
  }

  const call = async (body: Record<string, string>) => {
    if (inFlightRef.current) return null
    inFlightRef.current = true
    lastErrorRef.current = null
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/embeddedLogin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Request failed')
      return data
    } catch (e) {
      lastErrorRef.current = e instanceof Error ? e.message : 'Request failed'
      setError(lastErrorRef.current)
      return null
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }

  const sendCode = async () => {
    const sentFrom = step
    // The short form hands drivers to /driver-login?phone=… with the number
    // prefilled; this tells those sends apart from a number typed here.
    // Compare normalized: the input reformats the prefilled value on mount.
    const prefilled =
      Boolean(initialPhone) && unformatPhoneNumber(initialPhone) === e164
    if (inFlightRef.current || sendingRef.current) {
      trackSendSkipped('in-flight', sentFrom, prefilled)
      return
    }
    // Client-side cooldown (2 sends / minute) runs before anything else so a
    // blocked resend never spends a Turnstile token or a network round trip.
    if (getCooldownEndsAt()) {
      refreshCooldown()
      if (step === 'phone' && wasCodeRecentlySent(e164)) {
        trackSendSkipped('cooldown-to-otp', sentFrom, prefilled)
        setError(null)
        setStep('otp')
      } else {
        trackSendSkipped('cooldown', sentFrom, prefilled)
      }
      return
    }
    sendingRef.current = true
    // Lock the form now, not when `call` starts: the token wait below is a
    // real gap during which the phone field and buttons must already be
    // disabled (the SMS send limit relies on that).
    setLoading(true)
    setError(null)
    let data: Record<string, unknown> | null = null
    let requested = false
    try {
      // Waits for the widget if it is still verifying; `{}` when Turnstile
      // isn't configured. Only `start` sends an SMS, so only `start` carries
      // a token.
      const { token: turnstileToken, reason } = await turnstile.getToken()
      if (reason === 'needs-interaction') {
        // The widget is now visible and running its check. Don't send, and
        // don't reset (that would throw the in-progress check away).
        trackSendSkipped('needs-interaction', sentFrom, prefilled)
        setError(TURNSTILE_INTERACTION_MESSAGE)
        return
      }
      requested = true
      trackCodeRequested({
        step: sentFrom,
        prefilled,
        hasToken: Boolean(turnstileToken),
        clientReason: turnstileToken ? undefined : reason,
      })
      data = await call({
        action: 'start',
        phone: e164,
        companySlug: companySlug || '',
        companyId: companyId || '',
        ...(turnstileToken ? { turnstileToken } : {}),
        // Tokenless: tell the API why, so the log separates a slow widget
        // from a blocked script (and from a bot, which sends neither).
        ...(!turnstileToken && reason ? { turnstileClientReason: reason } : {}),
      })
    } finally {
      sendingRef.current = false
      // `call` clears loading on its own; this covers a token wait that
      // never reached `call`. The newUser branch below re-asserts loading.
      setLoading(false)
      // Single-use: mint a fresh token for a resend or a retry after an error.
      if (requested) turnstile.reset()
    }
    if (!data) {
      if (requested) {
        trackCodeResult({
          outcome: 'error',
          step: sentFrom,
          prefilled,
          error: lastErrorRef.current,
        })
      }
      return
    }
    if (data.newUser) {
      trackCodeResult({ outcome: 'new-user', step: sentFrom, prefilled })
      // Hold the loading state while the consumer resolves + navigates away.
      setLoading(true)
      setError(null)
      try {
        await onNewUser?.(e164)
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : 'Something went wrong. Please try again.'
        )
        setLoading(false)
      }
      return
    }
    recordCodeSent(e164)
    trackCodeResult({
      outcome: 'sent',
      step: sentFrom,
      prefilled,
      // This send used up the 2-per-minute allowance: the driver now sees
      // a countdown (taps on the disabled button can't be tracked).
      startedCooldown: Boolean(getCooldownEndsAt()),
    })
    refreshCooldown()
    setStep('otp')
  }

  const verifyCode = async (code = otp) => {
    const data = await call({
      action: 'verify',
      phone: e164,
      otp: code,
      companyId: companyId || '',
    })
    if (!data) return
    if (!data.access_token) {
      setError('Verification failed: no token received. Please try again.')
      return
    }
    setDriverToken(data.access_token)
    setLoading(true)
    setError(null)
    await reauthenticate()
    try {
      await onVerified({ e164, accessToken: data.access_token })
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Something went wrong. Please try again.'
      )
      setLoading(false)
    }
  }

  const editNumber = () => {
    setStep('phone')
    setOtp('')
    setError(null)
  }

  const startedRef = useRef(false)
  useEffect(() => {
    if (!autoStart || startedRef.current || !canSubmitPhone) return
    startedRef.current = true
    sendCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, canSubmitPhone])

  return {
    step,
    phone,
    otp,
    loading,
    error,
    canSubmitPhone,
    cooldownSeconds,
    phoneBlocked,
    setPhone,
    setOtp,
    sendCode,
    verifyCode,
    editNumber,
    turnstile,
  }
}
